import {
    CMM_CHECKS_SCORER_ID,
    CMM_JUDGE_SCORER_ID,
    CONVERSATIONAL_MM_AGENT,
    isContinuousScorerPassing,
} from '@fruitful/llm-service-shared';
import {
    buildPersonaFromTestCase,
    CONVERSATIONAL_QA_TEST_CASES,
    type ConversationalQATestCase,
} from '@fruitful/llm-service-shared/qa';
import { useRouter } from 'expo-router';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableExpandedRows,
    type DataTableRowClickEvent,
    type DataTableRowToggleEvent,
    type DataTableValueArray,
} from 'primereact/datatable';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    LabelRegular,
    LoadingSkeleton,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { QATestDetails } from '../components/QATestDetails';
import { ScorerValueWithRubric } from '../components/ScorerValueWithRubric';
import { useQASuite, useResumeQASuite } from '../hooks/useQASuite';
import { isRowTogglerClick } from '../utils/qaBatchTable';
import { getThreadScorerScore } from '../utils/qaThreadScorers';
import { formatScorerValue, getScorerValueColor } from '../utils/scoreUtils';
import {
    emptyQARun,
    type QARunState,
    type QARunStatus,
} from './conversationalQARunsStore';
import {
    BRANCH_LABELS,
    isCustomPromptName,
    summarizeBatch,
    toSimulationThreadMetadata,
} from './qaSimulationThreads';
import { useQASimulationThreads } from './useQASimulationThreads';

interface QABatchRow {
    testCase: ConversationalQATestCase;
    run: QARunState;
    defaultRun: QARunState | null;
}

const isQABatchRow = (value: unknown): value is QABatchRow => {
    if (value == null || typeof value !== 'object') {
        return false;
    }

    if (!('testCase' in value) || !('run' in value)) {
        return false;
    }

    const maybeTestCase = value.testCase;
    const maybeRun = value.run;

    if (maybeTestCase == null || typeof maybeTestCase !== 'object') {
        return false;
    }

    if (maybeRun == null || typeof maybeRun !== 'object') {
        return false;
    }

    return (
        'id' in maybeTestCase &&
        typeof maybeTestCase.id === 'string' &&
        'status' in maybeRun &&
        typeof maybeRun.status === 'string'
    );
};

const Text = ({
    children,
    color = '$gray1200',
}: {
    children: ReactNode;
    color?: string;
}) => <BodySmallRegular color={color}>{children}</BodySmallRegular>;

const CellSkeleton = ({ width }: { width: number }) => (
    <LoadingSkeleton width={width} height={12} borderRadius={6} />
);

const ScoreCell = ({
    scorers,
    scorerId = CMM_JUDGE_SCORER_ID,
}: {
    scorers: QARunState['scorers'];
    scorerId?: string;
}) => {
    const score = getThreadScorerScore(scorers, scorerId);
    if (score == null) return null;
    return (
        <ScorerValueWithRubric score={score} scorerId={scorerId}>
            <BodySmallRegular
                color={getScorerValueColor({ score, scorerId })}
                fontWeight="700"
            >
                {formatScorerValue({ score, scorerId })}
            </BodySmallRegular>
        </ScorerValueWithRubric>
    );
};

const renderPendingValue = (
    value: ReactNode,
    isRunning: boolean,
    skeletonWidth: number,
): ReactNode => {
    if (value != null) return value;
    if (isRunning) return <CellSkeleton width={skeletonWidth} />;
    return null;
};

const Centered = ({ children }: { children: ReactNode }) => (
    <XStack justifyContent="center" alignItems="center">
        {children}
    </XStack>
);

const renderAha = (
    row: QARunState,
    metrics: QARunState['metrics'],
): ReactNode => {
    const headline = metrics?.ahaMoment?.headline;
    if (headline == null || headline === '') {
        return row.status === 'running' ? <CellSkeleton width={140} /> : null;
    }

    return (
        <YStack>
            <Text>{headline}</Text>
            {metrics?.ahaMoment?.date != null &&
            metrics.ahaMoment.date !== '' ? (
                <BodySmallRegular color="$gray1200">
                    {metrics.ahaMoment.date}
                </BodySmallRegular>
            ) : null}
        </YStack>
    );
};

const renderCase = ({ testCase, run }: QABatchRow): ReactNode => {
    const canOpen = run.status !== 'pending';

    return (
        <BodySmallRegular
            color={canOpen ? '$blue900' : '$gray900'}
            fontWeight={canOpen ? '700' : '400'}
        >
            {testCase.name}
        </BodySmallRegular>
    );
};

const renderPath = ({ testCase }: QABatchRow): ReactNode => {
    return <Text>{testCase.path}</Text>;
};

const renderPersona = ({ run }: QABatchRow): ReactNode => {
    const participantTurns =
        run.metrics != null
            ? run.metrics.participantTurns + run.widgetInteractions
            : null;

    return renderPendingValue(
        participantTurns != null ? (
            <Text>{String(participantTurns)}</Text>
        ) : null,
        run.status === 'running',
        36,
    );
};

const renderAgent = ({ run }: QABatchRow): ReactNode => {
    return renderPendingValue(
        run.metrics != null ? (
            <Text>{String(run.metrics.agentTurns)}</Text>
        ) : null,
        run.status === 'running',
        36,
    );
};

const renderBranch = ({ run }: QABatchRow): ReactNode => {
    const branchLabel =
        run.metrics?.branch != null
            ? (BRANCH_LABELS[run.metrics.branch] ?? run.metrics.branch)
            : null;

    return renderPendingValue(
        branchLabel != null ? <Text>{branchLabel}</Text> : null,
        run.status === 'running',
        90,
    );
};

const renderSteps = ({ run }: QABatchRow): ReactNode => {
    const stepsCompleted = run.metrics?.stepsCompleted;
    const stepsTotal = run.metrics?.stepsTotal;

    const value =
        stepsCompleted != null && stepsTotal != null ? (
            <Text
                color={stepsCompleted >= stepsTotal ? '$green900' : '$gray1200'}
            >
                {`${stepsCompleted}/${stepsTotal}`}
            </Text>
        ) : null;

    return renderPendingValue(value, run.status === 'running', 30);
};

const renderAhaMoment = ({ run }: QABatchRow): ReactNode => {
    return renderAha(run, run.metrics);
};

const renderScore = ({ run }: QABatchRow): ReactNode => {
    // A real score always wins — never hide it behind a (possibly non-terminal)
    // error flag.
    if (run.scorers != null) {
        return (
            <Centered>
                <ScoreCell scorers={run.scorers} />
            </Centered>
        );
    }

    // Only surface ERROR once the run has truly finished failing. While it's
    // still running (including the server-side eval before `done`), show the
    // pending skeleton instead of a premature red ERROR.
    if (run.status === 'error') {
        return (
            <Centered>
                <BodySmallRegular color="$red900" fontWeight="700">
                    ERROR
                </BodySmallRegular>
            </Centered>
        );
    }

    return (
        <Centered>
            {renderPendingValue(null, run.status === 'running', 40)}
        </Centered>
    );
};

const renderDefaultScore = ({ defaultRun }: QABatchRow): ReactNode => {
    return (
        <Centered>
            {defaultRun?.scorers != null ? (
                <ScoreCell scorers={defaultRun.scorers} />
            ) : null}
        </Centered>
    );
};

const renderChecks = ({ run }: QABatchRow): ReactNode => (
    <Centered>
        {renderPendingValue(
            run.scorers != null ? (
                <ScoreCell
                    scorers={run.scorers}
                    scorerId={CMM_CHECKS_SCORER_ID}
                />
            ) : null,
            run.status === 'running',
            36,
        )}
    </Centered>
);

const renderTestDetails = ({ testCase, run }: QABatchRow): ReactNode => (
    <QATestDetails
        fields={[
            ...(run.status === 'error' && run.error != null
                ? [{ label: 'Error', value: run.error }]
                : []),
            { label: 'Scenario', value: buildPersonaFromTestCase(testCase) },
            {
                label: 'Expected / judge criteria',
                value: testCase.outcomeInstructions,
            },
        ]}
    />
);

const formatRanAt = (value: Date | null): string =>
    value == null ? '' : value.toLocaleString();

const toRunStatus = (
    hasScorers: boolean,
    suiteStatus?: 'pending' | 'running' | 'completed' | 'error',
): QARunStatus => {
    if (hasScorers || suiteStatus === 'completed') {
        return 'done';
    }
    return suiteStatus ?? 'pending';
};

export default function ConversationalOnboardingQABatchPage({
    batchId,
}: {
    batchId: string;
}) {
    const router = useRouter();
    const [expandedRows, setExpandedRows] = useState<
        DataTableExpandedRows | DataTableValueArray | undefined
    >(undefined);
    const { data: suite, isLoading: isLoadingSuite } = useQASuite(
        CONVERSATIONAL_MM_AGENT.id,
        batchId,
    );
    const resumeSuite = useResumeQASuite(CONVERSATIONAL_MM_AGENT.id, batchId);
    const isRunning =
        suite?.status === 'pending' || suite?.status === 'running';

    const { data: persistedThreads = [], isLoading: isLoadingPersistedRuns } =
        useQASimulationThreads(5_000);

    const batchThreads = useMemo(
        () =>
            persistedThreads.filter(
                (thread) =>
                    toSimulationThreadMetadata(thread.metadata)?.batchId ===
                    batchId,
            ),
        [persistedThreads, batchId],
    );

    const summary = useMemo(
        () => summarizeBatch(batchId, batchThreads),
        [batchId, batchThreads],
    );

    const batchPromptName = suite?.promptName ?? summary.promptName;

    const suiteCasesById = useMemo(
        () =>
            new Map(
                suite?.cases.map((testCase) => [testCase.caseId, testCase]) ??
                    [],
            ),
        [suite],
    );

    const persistedRunsForBatch = useMemo(() => {
        const byTestCaseId: Record<string, QARunState> = {};

        for (const thread of batchThreads) {
            const metadata = toSimulationThreadMetadata(thread.metadata);
            if (metadata?.qaTestCaseId == null) {
                continue;
            }

            if (byTestCaseId[metadata.qaTestCaseId] != null) {
                continue;
            }

            const suiteCase = suiteCasesById.get(metadata.qaTestCaseId);
            byTestCaseId[metadata.qaTestCaseId] = {
                ...emptyQARun(),
                status: toRunStatus(
                    metadata.scorers != null,
                    suiteCase?.status,
                ),
                threadId: suiteCase?.threadId ?? thread.id,
                promptName: metadata.promptName ?? null,
                metrics: metadata.metrics ?? null,
                scorers: metadata.scorers ?? null,
                error: suiteCase?.error ?? null,
            };
        }

        for (const suiteCase of suite?.cases ?? []) {
            if (byTestCaseId[suiteCase.caseId] != null) {
                continue;
            }
            byTestCaseId[suiteCase.caseId] = {
                ...emptyQARun(),
                status: toRunStatus(false, suiteCase.status),
                threadId: suiteCase.threadId ?? null,
                promptName: suite?.promptName ?? null,
                error: suiteCase.error ?? null,
            };
        }

        return byTestCaseId;
    }, [batchThreads, suite, suiteCasesById]);

    /** Latest default-prompt run per test case, across all batches (for comparison). */
    const latestDefaultPersistedRunsByTestCase = useMemo(() => {
        const byTestCaseId: Record<string, QARunState> = {};

        for (const thread of persistedThreads) {
            const metadata = toSimulationThreadMetadata(thread.metadata);
            if (metadata?.qaTestCaseId == null) {
                continue;
            }

            if (isCustomPromptName(metadata.promptName)) {
                continue;
            }

            if (byTestCaseId[metadata.qaTestCaseId] != null) {
                continue;
            }

            if (metadata.metrics == null || metadata.scorers == null) {
                continue;
            }

            byTestCaseId[metadata.qaTestCaseId] = {
                ...emptyQARun(),
                status: 'done',
                threadId: thread.id,
                promptName: null,
                metrics: metadata.metrics,
                scorers: metadata.scorers,
            };
        }

        return byTestCaseId;
    }, [persistedThreads]);

    const handleOpenRun = useCallback(
        (target: string, isLive: boolean) => {
            router.push({
                pathname: '/ai/threads/[threadId]',
                params: {
                    threadId: target,
                    agentId: CONVERSATIONAL_MM_AGENT.id,
                    ...(isLive ? { live: '1' } : {}),
                },
            });
        },
        [router],
    );

    const visibleTestCases =
        suite != null
            ? CONVERSATIONAL_QA_TEST_CASES
            : CONVERSATIONAL_QA_TEST_CASES.filter(
                  (testCase) => persistedRunsForBatch[testCase.id] != null,
              );

    const rows: QABatchRow[] = visibleTestCases.map((testCase) => {
        const persistedRun = persistedRunsForBatch[testCase.id];

        return {
            testCase,
            run: persistedRun ?? emptyQARun(),
            defaultRun:
                latestDefaultPersistedRunsByTestCase[testCase.id] ?? null,
        };
    });

    const showDefaultScoreColumn = isCustomPromptName(batchPromptName);

    const passed = rows.filter((row) => {
        const score = getThreadScorerScore(
            row.run.scorers,
            CMM_JUDGE_SCORER_ID,
        );
        return score != null && isContinuousScorerPassing(score);
    }).length;
    const failed = rows.filter((row) => {
        const score = getThreadScorerScore(
            row.run.scorers,
            CMM_JUDGE_SCORER_ID,
        );
        return score != null && !isContinuousScorerPassing(score);
    }).length;

    return (
        <YStack
            flex={1}
            minHeight={0}
            height="100%"
            width="100%"
            gap={8}
            padding={8}
            overflow="hidden"
        >
            <XStack alignItems="center" gap={16} flexWrap="wrap">
                <YStack>
                    <LabelRegular color="$gray700">Prompt</LabelRegular>
                    <BodyMediumEmphasized>
                        {isCustomPromptName(batchPromptName)
                            ? batchPromptName
                            : 'Default'}
                    </BodyMediumEmphasized>
                </YStack>
                <YStack>
                    <LabelRegular color="$gray700">Ran</LabelRegular>
                    <BodySmallRegular>
                        {isRunning
                            ? 'In progress'
                            : formatRanAt(
                                  summary.ranAt ??
                                      (suite != null
                                          ? new Date(suite.createdAt)
                                          : null),
                              )}
                    </BodySmallRegular>
                </YStack>
                <YStack>
                    <LabelRegular color="$gray700">Result</LabelRegular>
                    <BodySmallRegular>
                        <BodySmallRegular color="$green900" fontWeight="700">
                            {String(passed)}
                        </BodySmallRegular>
                        {' passed · '}
                        <BodySmallRegular color="$red900" fontWeight="700">
                            {String(failed)}
                        </BodySmallRegular>
                        {` failed · ${String(rows.length)} tests`}
                    </BodySmallRegular>
                </YStack>
                {isRunning ? (
                    <XStack marginLeft="auto" alignItems="center" gap={8}>
                        <Spinner />
                    </XStack>
                ) : null}
                {suite?.status === 'incomplete' ? (
                    <Button
                        marginLeft="auto"
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        width="auto"
                        text="Resume incomplete"
                        disabled={resumeSuite.isPending}
                        onPress={() => {
                            resumeSuite.mutate();
                        }}
                    />
                ) : null}
            </XStack>

            {(isLoadingPersistedRuns || isLoadingSuite) && rows.length === 0 ? (
                <XStack justifyContent="center" padding="$4">
                    <Spinner />
                </XStack>
            ) : null}

            <YStack flex={1} minHeight={0}>
                <DataTable
                    value={rows}
                    dataKey="testCase.id"
                    size="small"
                    showGridlines
                    stripedRows
                    scrollable
                    scrollHeight="flex"
                    tableStyle={{ width: '100%' }}
                    emptyMessage="No runs in this batch."
                    expandedRows={expandedRows}
                    onRowToggle={(event: DataTableRowToggleEvent) => {
                        setExpandedRows(event.data);
                    }}
                    rowExpansionTemplate={(data: unknown): ReactNode =>
                        isQABatchRow(data) ? renderTestDetails(data) : null
                    }
                    rowClassName={(row: QABatchRow) => {
                        return row.run.status !== 'pending'
                            ? 'qa-batch-clickable-row'
                            : '';
                    }}
                    onRowClick={(event: DataTableRowClickEvent) => {
                        if (isRowTogglerClick(event)) {
                            return;
                        }

                        const rowData: unknown = event.data;
                        if (!isQABatchRow(rowData)) {
                            return;
                        }

                        if (
                            rowData.run.status !== 'pending' &&
                            rowData.run.threadId != null
                        ) {
                            handleOpenRun(
                                rowData.run.threadId,
                                rowData.run.status === 'running',
                            );
                        }
                    }}
                >
                    <Column
                        expander
                        style={{ width: '1%', whiteSpace: 'nowrap' }}
                    />
                    <Column
                        field="testCase.name"
                        header="Case"
                        body={renderCase}
                        style={{ width: '19%' }}
                    />
                    <Column
                        field="testCase.path"
                        header="Path"
                        body={renderPath}
                        style={{ width: '8%' }}
                    />
                    <Column
                        field="run.metrics.participantTurns"
                        header="Persona"
                        body={renderPersona}
                        style={{ width: '8%' }}
                    />
                    <Column
                        field="run.metrics.agentTurns"
                        header="Fruitful"
                        body={renderAgent}
                        style={{ width: '8%' }}
                    />
                    <Column
                        field="run.metrics.branch"
                        header="Branch"
                        body={renderBranch}
                        style={{ width: '11%' }}
                    />
                    <Column
                        field="run.metrics.stepsCompleted"
                        header="Steps"
                        body={renderSteps}
                        alignHeader="center"
                        style={{ width: '6%', textAlign: 'center' }}
                    />
                    <Column
                        field="run.metrics.ahaMoment.headline"
                        header="Aha moment"
                        body={renderAhaMoment}
                        style={{ width: '12%' }}
                    />
                    <Column
                        field="run.scorers"
                        header="Score"
                        body={renderScore}
                        alignHeader="center"
                        style={{
                            width: showDefaultScoreColumn ? '6%' : '8%',
                            textAlign: 'center',
                        }}
                    />
                    <Column
                        field="run.scorers"
                        header="Checks"
                        body={renderChecks}
                        alignHeader="center"
                        style={{ width: '9%', textAlign: 'center' }}
                    />
                    {showDefaultScoreColumn ? (
                        <Column
                            field="defaultRun.scorers"
                            header="Default"
                            body={renderDefaultScore}
                            alignHeader="center"
                            style={{ width: '6%', textAlign: 'center' }}
                        />
                    ) : null}
                </DataTable>
                <style>{`
                    .p-datatable .qa-batch-clickable-row {
                        cursor: pointer;
                    }
                    .p-datatable .qa-batch-clickable-row:hover {
                        background-color: #E1FDEA !important;
                    }
                `}</style>
            </YStack>
        </YStack>
    );
}
